import React from "react"
import { Link } from "react-router-dom"

type Props = {
  children: React.ReactNode
  variant?: "primary" | "outline" | "outline-primary"
  href?: string
  onClick?: () => void
  type?: "button" | "submit"
  disabled?: boolean
  className?: string
}

const LandingButton: React.FC<Props> = ({
  children,
  variant = "primary",
  href,
  onClick,
  type = "button",
  disabled = false,
  className = "",
}) => {
  const baseClasses =
    "relative inline-flex items-center justify-center rounded-lg px-5 py-2.5 font-dm-sans text-sm font-semibold tracking-wide transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"

  const variantClasses =
    variant === "primary"
      ? "bg-gradient-to-b from-[#1a5ee8] to-dc-primary-900 text-white shadow-[0_6px_20px_-6px_rgba(0,75,214,0.55)] hover:shadow-[0_8px_24px_-6px_rgba(0,75,214,0.6)]"
      : variant === "outline-primary"
        ? "border border-dc-primary-900 bg-white text-dc-primary-900 shadow-none hover:bg-dc-primary-900/5"
        : "border border-white/80 bg-white/5 text-white shadow-none backdrop-blur-sm hover:border-white hover:bg-white/10"

  const classes = `${baseClasses} ${variantClasses} ${className}`

  if (href) {
    const isExternal = href.startsWith("http")
    const isMailtoOrTel =
      href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("sms:")
    const isAnchor = href.startsWith("#") || href.includes("#")

    if (isMailtoOrTel) {
      return (
        <a href={href} className={classes}>
          {children}
        </a>
      )
    }

    if (isExternal) {
      return (
        <a href={href} className={classes} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      )
    }

    if (isAnchor) {
      const to = href.startsWith("#") ? { hash: href.slice(1) } : href
      return (
        <Link to={to} className={classes}>
          {children}
        </Link>
      )
    }

    return (
      <Link to={href} className={classes}>
        {children}
      </Link>
    )
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={classes}>
      {children}
    </button>
  )
}

export default LandingButton
